/* eslint-disable eqeqeq */
// 3rd-party modules
import { message, Tooltip } from "antd";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useSearchParams } from 'react-router-dom';

// project modules
import Button from "../shared/button";
import Input from "../shared/inputs/input";
import Loader from "../shared/loader";
import Select from "../shared/inputs/select";
import yup from "../../plugins/yup";
import { apiCall } from "../../helpers/apiHelper";
import Radio, { RadioType } from '../../components/shared/inputs/radio';


// apis
import * as AccountKiboshDeviceApi from '../../apis/accountKiboshDeviceApi';
import * as CommonValueApi from '../../apis/commonValueApi';
import * as DashApi from '../../apis/dashApi';

// models
import { accountKiboshDeviceInsertViewModel } from "../../models/types/accountKiboshDevice";
import { AccountKiboshDevice } from "../../models/accountKiboshDevice";
import { ApiResponse } from "../../models/response";
import { CommonValue } from "../../models/commonValue";
import { useSelector } from "react-redux";
import { RootState } from "../../stores";
import { User } from "../../models/auth";
import { DataSourceRequest } from "../../models/dataSourceRequest";

type Props = {
  accountId?: number;
  cancelText?: string;
  okText?: string;
  refresh?: boolean;
  showCancelButton?: boolean;
  selectedDevice?: AccountKiboshDevice | null;
  autoCreateDeviceMode?: string | null;
  isSelectedKiboshDevice?: boolean;
  onCancel?: () => void;
  onSave?: (kiboshDeviceDetails?: any) => void;
};

export default function AccountDevice({ accountId, cancelText = "Cancel", okText = "Save Changes", refresh, selectedDevice, autoCreateDeviceMode, showCancelButton = true, isSelectedKiboshDevice = false, onCancel, onSave }: Props) {
  const schema = yup.object().shape({
    kiboshDeviceName: yup.string().label("Device Name").max(256).required(),
    kiboshDeviceReference: yup.string().label("Device Reference").max(256).required()
      .test("exist-in-list", "This reference number should be chosen from list.", function (val) {
        if (accountKiboshDevices.filter(x => !x.hasActiveSubscription).map(x => x.kiboshDeviceReference?.toLocaleLowerCase()).includes(val.toLocaleLowerCase())) {
          return false;
        }

        return true;
      })
      .test("active-device", "This reference number belongs to an active Kibosh device under your account.", function (val) {
        if (accountKiboshDevices.filter(x => x.hasActiveSubscription).map(x => x.kiboshDeviceReference?.toLocaleLowerCase()).includes(val.toLocaleLowerCase())) {
          return false;
        }

        return true;
      }),
    kiboshDeviceTypeId: yup.string().label("Device Type").required(),
  });
  const { control, handleSubmit, setValue, reset } = useForm<accountKiboshDeviceInsertViewModel | any>({
    resolver: yupResolver(schema),
    defaultValues: !isSelectedKiboshDevice ? selectedDevice : {}
  });

 const [searchParams] = useSearchParams();


  const [deviceTypes, setDeviceTypes] = useState<CommonValue[]>([]);
  const [selectedDeviceType, setSelectedDeviceType] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [lockDeviceDetails, setLockDeviceDetails] = useState(isSelectedKiboshDevice);
  const [loadingDevicesTypeList, setLoadingDevicesTypeList] = useState(false);
  const [loadingAccountKiboshDevices, setLoadingAccountKiboshDevices] = useState(false);
  const [accountKiboshDevices, setAccountKiboshDevices] = useState<AccountKiboshDevice[]>([]);
  const [accountKiboshDeviceDatasource, setAccountKiboshDeviceDatasource] = useState<any[]>([]);
  const [selectedAccountKiboshDevice, setSelectedAccountKiboshDevice] = useState<AccountKiboshDevice | null>(null);
  const user = useSelector(
    (state: RootState) => state.user.currentUser
  ) as User;
  const currentKiboshDevice = useSelector((state: RootState) => state.user?.kiboshDevice) || null;

  const abortController = new AbortController();

  useEffect(() => {
    getDeviceTypesAsync();
    getAccountKiboshDevicesAsync();

    let mode = searchParams.get("mode");
    mode = mode ? mode.toLowerCase() : selectedDeviceType
    mode = mode ? mode : "router"




    setSelectedDeviceType( mode );
    setValue("kiboshDeviceTypeId", 1);

  }, []);

  useEffect(() => {
    if (deviceTypes?.length && autoCreateDeviceMode) {
      autoCreateKiboshDeviceAsync(autoCreateDeviceMode);
    }
  }, [deviceTypes])

  useEffect(() => {
    const lockDetails = selectedDeviceType.toLocaleLowerCase() === "vpn" || selectedDeviceType.toLocaleLowerCase() === "dns";

    setLockDeviceDetails(lockDetails);

    if (lockDetails) {
      getNewDeviceIdAsync(selectedDeviceType);
    }

  }, [selectedDeviceType]);

  const autoCreateKiboshDeviceAsync = async (deviceType: string) => {
    const data = await getNewDeviceId(deviceType, abortController.signal);

    // eslint-disable-next-line eqeqeq
    const typeId = deviceTypes.find(x => x.value == deviceType)?.commonValueId;

    setValue("kiboshDeviceTypeId", typeId);
    setValue("kiboshDeviceName", data);
    setValue("kiboshDeviceReference", data);

    handleSubmit(onSubmit)();
  }

  const getNewDeviceIdAsync = async (deviceType: string) => {
    const data = await getNewDeviceId(deviceType, abortController.signal);

    setValue("kiboshDeviceName", data);
    setValue("kiboshDeviceReference", data);
  }

  const getNewDeviceId = async (deviceType: string, abortSignal?: AbortSignal) => {
    setLoading(true);
    const response = await apiCall(DashApi.getNextUnusedIdAsync(deviceType, abortSignal));
    setLoading(false);

    return response.success ? response.data?.value : "";
  }

  const getDeviceTypesAsync = async () => {
    const data = await getDeviceTypes(abortController.signal);

    setDeviceTypes(data);
  }

  const getDeviceTypes = async (abortSignal?: AbortSignal) => {
    setLoadingDevicesTypeList(true);
    const response = await apiCall(CommonValueApi.getDeviceTypes(abortSignal));
    setLoadingDevicesTypeList(false);

    return response.success ? CommonValue.toArrayOfClass(response.data?.value || []) : [];
  }

  const getAccountKiboshDevicesAsync = async () => {
    const data = await getAccountKiboshDevices({}, abortController.signal);
    
 
    if (isSelectedKiboshDevice) {

      setSelectedAccountKiboshDevice(data.find(x => x.kiboshDeviceReference == selectedDevice?.kiboshDeviceReference) || null);
    } else {
      setSelectedAccountKiboshDevice(data.find(x => (!x.hasActiveSubscription) && x.kiboshDeviceTypeCode?.toLowerCase() == "router") || null);
    }
    setAccountKiboshDevices(data);

    // Step 1: Sort by accountKiboshDeviceId
    const sorted = [...data].sort((a, b) => a.accountKiboshDeviceId - b.accountKiboshDeviceId);

    // Step 2: Group by lastSubscriptionId
    const groupedMap = new Map<string, AccountKiboshDevice[]>();
    for (const device of sorted) {
      const key = device.lastSubscriptionId || uuidv4();
      if (!key) continue; // Skip null, undefined, or empty string
      if (!groupedMap.has(key)) {
        groupedMap.set(key, []);
      }
      groupedMap.get(key)!.push(device);
    }

    // Step 3: Transform to desired result format
    const result: any[] = [];
    groupedMap.forEach((group, lastSubscriptionId) => {
      const [first, ...rest] = group;
      result.push({
        lastSubscriptionId,
        firstItemId: first.accountKiboshDeviceId,
        firstItemName: first.kiboshDeviceName,
        firstItemReference: first.kiboshDeviceReference,
        extraVpnCount: rest.length,
        extraVpnReferences: rest.map(r => r.kiboshDeviceReference).join(', ')
      });
    });

    const sortedResult = [...result].sort((a, b) => b.firstItemId - a.firstItemId);


    const kdid = searchParams.get("kdid");
    let kd = kdid == null || kdid== "" ? null : data.find(x => x.kiboshDeviceReference == kdid)
    if(kd != null) {
      let kdSub = sortedResult.find(x => x.firstItemReference == kd?.kiboshDeviceReference || x.extraVpnReferences.includes(kd?.kiboshDeviceReference) );
      let dt = "router"

      if(kdSub != null) {
        let sakd = data.find(x => x.kiboshDeviceReference == kdSub?.firstItemReference)
        if(kdSub.firstItemReference?.startsWith("v-")) { dt = "vpn" }
        if(kdSub.firstItemReference?.startsWith("d-")) { dt = "dns" }
        setSelectedAccountKiboshDevice(sakd || null)
        
      } else {
        if(kd.kiboshDeviceReference?.startsWith("v-")) { dt = "vpn" }
        if(kd.kiboshDeviceReference?.startsWith("d-")) { dt = "dns" }
        setSelectedAccountKiboshDevice(kd || null)
      }

      setSelectedDeviceType(dt)


    }

    setAccountKiboshDeviceDatasource(sortedResult);
  }

  const getAccountKiboshDevices = async (request?: DataSourceRequest, abortSignal?: AbortSignal) => {
    setLoadingAccountKiboshDevices(true);
    const response = await apiCall(AccountKiboshDeviceApi.getAccountKiboshDevices(user?.accountId!, request, abortSignal));
    setLoadingAccountKiboshDevices(false);

    return response.success ? AccountKiboshDevice.toArrayOfClass(response.data?.value || []) : [];
  };

  const onCancelClick = () => {
    if (onCancel) {
      onCancel();
    }
  }

  const onDeviceTypeChange = (e: any) => {


    setValue("kiboshDeviceName", "");
    setValue("kiboshDeviceReference", "");
    // eslint-disable-next-line eqeqeq
    const deviceType = deviceTypes.find(x => x.commonValueId == e.target.value);
    setSelectedDeviceType(deviceType?.value || "");
    setValue("kiboshDeviceTypeId", e.target.value);
  }

  const onSubmit: SubmitHandler<accountKiboshDeviceInsertViewModel> = async (formData: accountKiboshDeviceInsertViewModel) => {
    setLoading(true);

    if (selectedDeviceType === "router") {
      let response: ApiResponse;

      if (!selectedDevice?.accountKiboshDeviceId) {
        //TODO: Make sure Kibosh device has not regestered before
        formData.accountId = accountId!;
        formData.accountKiboshDeviceId = 0;

        response = await apiCall(AccountKiboshDeviceApi.insertAccountKiboshDevice(formData, abortController.signal));
      } else
        response = await apiCall(AccountKiboshDeviceApi.updateAccountKiboshDevice(formData, abortController.signal));

      if (response.success) {
        message.success(`Kibosh Device ${!selectedDevice?.accountKiboshDeviceId ? 'added' : 'edited'} successfully.`);

        const deviceData = {
          ...AccountKiboshDevice.toClass(response.data?.value),
          kiboshDeviceTypeCode : selectedDeviceType
        }

        if (onSave) onSave({ device: deviceData, type: selectedDeviceType, isSelectedKiboshDevice: false });
      } else
        message.error(response.error?.value);
    }
    else {
      // Avoid Save and just create the object for VPN and DNS
      const deviceData: AccountKiboshDevice = {
        kiboshDeviceReference : formData.kiboshDeviceReference,
        kiboshDeviceName : formData.kiboshDeviceName,
        accountId : accountId!,
        kiboshDeviceTypeId : formData.kiboshDeviceTypeId,
        kiboshDeviceTypeCode : selectedDeviceType || autoCreateDeviceMode?.toLowerCase()
      }

      if (onSave) onSave({ device: deviceData, type: selectedDeviceType || autoCreateDeviceMode?.toLowerCase(), isSelectedKiboshDevice: false });
    }

    setLoading(false);
  };

  const onAccountKiboshDeviceChange = (kiboshDeviceReference: any) => {
    setSelectedAccountKiboshDevice(accountKiboshDevices.find(x => x.kiboshDeviceReference == kiboshDeviceReference) || null);
  }

  const onNextButtonClick = () => {
    reset();
    const deviceType = deviceTypes.find(x => x.commonValueId == selectedAccountKiboshDevice?.kiboshDeviceTypeId);
    if (onSave) onSave({ device: AccountKiboshDevice.toClass(selectedAccountKiboshDevice!), type: deviceType?.value || "", isSelectedKiboshDevice: true });
  }

  const onSubscribeLaterClick = () => {
    window.location.href= !!currentKiboshDevice && currentKiboshDevice.kiboshDeviceTypeCode?.toLowerCase() == "router" ?  "/portal/dash?tab=settings" : "/portal"
  }


  const adjustDeviceTypeOrder = (deviceTypes:any) => {
    if(!deviceTypes) {
      return []
    } else {
      let adj = deviceTypes.toSorted((a:any, b:any) => a.commonValueId - b.commonValueId );
      let a = adj.pop()
      let b = adj.pop()
      if(a != null && b != null) {
        adj.push(a)
        adj.push(b)
        return adj
      } else {
        return deviceTypes
      }
    }
  }

  return (
    <>
      {(loading || loadingAccountKiboshDevices) && <Loader />}
      <space data-height="40"></space>
      {
        !!accountKiboshDeviceDatasource?.filter(x => !x.hasActiveSubscription).length &&
        <group data-gap="10" data-direction="column" data-space-bottom="10">
          <text data-wrap="wrap" data-line="1.5" data-light="">
            Here you can choose a device from the list below OR create a new one from the lower form.
          </text>
          <Select
            autoComplete=""
            label="Kibosh Device"
            labelPosition="left"
            dataLength="500"
            onChange={onAccountKiboshDeviceChange}
            loading={loadingAccountKiboshDevices}
            allowSearch={true}
            allowClear={true}
            addEmptyOption={true}
            defaultValue={selectedAccountKiboshDevice  ? selectedAccountKiboshDevice?.kiboshDeviceReference : undefined}
            options={
              accountKiboshDeviceDatasource?.filter(x => !x.hasActiveSubscription).map((item) => {
                return {
                  text: `${item.firstItemName!} (${item.firstItemReference!})` + (!!item.extraVpnCount ? ` + ${item.extraVpnCount} VPNs(${item.extraVpnReferences})` : ""),
                  value: item.firstItemReference!,
                };
              }) || []
            }
            button={
              <Button
                material
                icon="refresh"
                onClick={getAccountKiboshDevicesAsync}
              />
            }
          />
          <separator data-horizontal=""></separator>
        </group>
      }
      <group data-gap="10" data-direction="column" data-margin-bottom="10">
        <text data-wrap="wrap" data-length="700" data-line="1.5">
          Kibosh Product to activate:
        </text>
      </group>
      <form
        data-length="500"
        data-type="group"
      >
        <group
          data-direction="column"
          data-align="start"
          data-gap="10">


          <group data-max-length="600">
          {
              adjustDeviceTypeOrder(deviceTypes).map((item:any) => (
               <group data-max-length="600">
                <Radio
                  name="kiboshDeviceTypeIdRadio"
                  radioValue={ item.commonValueId! }
                  label={item.valueCaption!}
                  control={control}
                  checked={ selectedDeviceType === item.value! || ( (selectedDeviceType === null || selectedDeviceType === "") && item.value! === "router") }
                  onChange={onDeviceTypeChange}
                  disabled={!!selectedAccountKiboshDevice}
                />
                </group>
              ))
          }
          {
            <group data-max-length="600" style={{ display: "none" }} >
              <Select
                control={control}
                label="Kibosh Product"
                labelPosition="left"
                dataLength="forcefit"
                dataFit="1"
                name="kiboshDeviceTypeId"
                disabled={!!selectedAccountKiboshDevice}
                allowSearch={true}
                loading={loadingDevicesTypeList}
                onChange={onDeviceTypeChange}
                options={
                  deviceTypes?.map((item) => {
                    return {
                      text: item.valueCaption!,
                      value: item.commonValueId!,
                    };
                  }) || []
                }
              />
            </group>
          }
          </group>



          <group data-gap="10" data-max-length="600"  style={{ display: lockDeviceDetails ? "none" : "block" }} >
            <group data-align="end">
              <Input
                control={control}
                name="kiboshDeviceName"
                label={lockDeviceDetails ? "Device Name" : "Create a Friendly name"}
                dataLength="forcefit"
                disabled={!!selectedAccountKiboshDevice}
                dataFit="1"
                size="large"
                readOnly={lockDeviceDetails}
              />
            </group>
            <group data-align="end" data-gap="10">
              <Input
                control={control}
                name="kiboshDeviceReference"
                label="Router ID"
                dataLength="auto"
                disabled={!!selectedAccountKiboshDevice}
                dataFit="1"
                size="large"
                readOnly={lockDeviceDetails}
              />
              {
                !lockDeviceDetails &&
                <Tooltip title="Your Kibosh Router ID is located under your Kibosh Router."><div className='button micro' data-color="gray"><icon>info</icon></div></Tooltip>
              }
            </group>
          </group>
        </group>

        <space data-height="40"></space>
        <group data-length="auto" data-direction="row" data-gap="10">
          {!!showCancelButton && <Button large material outline data-length="forcefit" onClick={onCancelClick}>
            <text>{cancelText}</text>
          </Button>}
          <Button large material primary data-length="forcefit" onClick={selectedAccountKiboshDevice ? onNextButtonClick : handleSubmit(onSubmit)}>
            <text>{okText}</text>
          </Button>
          <Button large material primary data-length="forcefit" onClick={onSubscribeLaterClick}>
            <text>Subscribe Later</text>
          </Button>
        </group>
      </form>
    </>
  );
}
